import { ApiOptions, CspError, CspErrorType, Maybe, RestOptions, StateAssert, User } from '@csp/csp-common-model';
import { getTelemetryService } from '@csp/csp-common-telemetry';
import { toRestOptions } from '@csp/csp-fe-auth';
import { ConsentRestServiceV1 } from '@csp/dmdp-api-client';
import { RoleType } from '@csp/dmdp-api-common-dto';
import {
  ConsentActionCriteriaFieldV1,
  ConsentActionQueryTypeV1,
  ConsentArtifactCriteriaFieldV1,
  ConsentArtifactQueryTypeV1,
} from '@csp/dmdp-api-consent-dto';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import {
  AnsweredConsentsV1,
  ApprovedConsentsV1,
  ConsentFeatureTypeV1,
  ConsentFormV1,
  GetAllAnsweredConsentsQueryInV1,
  GetPendingConsentsQueryInV1,
  GetScheduledConsentsQueryInV1,
  PendingConsentsV1,
  ScheduledConsentsV1,
  SkippedConsentInputV1,
} from '@csp/dto';
import { getPodGenericResourceContentBlock, PatientContentService } from '@csp/csp-pod-content-service';
import { CspLogger } from '@csp/csp-be-util';
import { keyBy, uniq } from 'lodash';
import {
  fromAnsweredConsentsResponse,
  fromApprovedConsentResponse,
  fromConsentResponse,
  fromLegacyConsentsToAnsweredConsents,
  fromRequestsToSortedAnsweredConsents,
  mapConsentActionV1WithArtifactMeta,
  mapConsentsWithArtifacts,
  toGetScheduledConsentsResponse,
  toGetUnresolvedConsentsResponse,
  toWebAppSystemTextIntersection,
} from '../mapper/ConsentResponseMapper';
import { ConsentActionWithArtifactMeta } from '../model/ConsentAction';
import { ConsentPlatform } from '../model/ConsentPlatform';
import { ConsentRequest } from '../model/ConsentRequest';
import { ConsentSchedule } from '../model/ConsentSchedule';
import { ScheduledConsent } from '../model/ScheduledConsent';
import { consentFilterUtil } from '../util/consentFilterUtil';
import { consentShouldBePrompted } from '../util/utils';
import { ConsentArtifactService } from './ConsentArtifactService';
import { ConsentContentService } from './ConsentContentService';
import { ConsentScheduleService } from './ConsentScheduleService';
import { LegacyConsentService } from './LegacyConsentService';
import { getTextConsents, toScheduledTextConsents } from './text-consent/TextConsent';

const getUserRole = (user: User): RoleType => {
  const role = user.roles[0];

  StateAssert.notNull(role, 'No role found on user');

  return role;
};

/**
 * Get the legacy consents (EULA + PP) and all consents in the active schedule.
 * Filter them on role, platform, features and none approved.
 * Get and map artifacts and cms content into the consents.
 * Sort by EULA > PP > mandatory consents true > false
 * @deprecated
 */
const getPendingConsents = async (
  { onFeatures }: GetPendingConsentsQueryInV1,
  user: User,
  platform: ConsentPlatform,
  apiOptions?: ApiOptions,
  logger?: CspLogger,
): Promise<PendingConsentsV1> => {
  const { locale } = user.localization;
  const role = getUserRole(user);

  const legacyConsents = await LegacyConsentService.getPendingLegacyConsents(
    user,
    platform,
    [ConsentType.EULA, ConsentType.PRIVACY_POLICY],
    apiOptions,
  );

  const activeSchedules: ConsentSchedule[] = await ConsentScheduleService.getActiveSchedules(
    user.firstOrgId,
    apiOptions,
  );

  const requests = await consentFilterUtil.filterActiveNoneApprovedRequests(
    activeSchedules,
    role,
    platform,
    user,
    onFeatures,
    apiOptions,
  );

  const activeArtifactCodes = getArtifactCodesFromRequests(requests);

  const cmsConsentSystemTexts = await ConsentContentService.getAppSystemTextsForConsent(locale, apiOptions);

  if (!cmsConsentSystemTexts) {
    throw new CspError(CspErrorType.NOT_FOUND, 'Consent system texts does not exist in CMS');
  }

  if (!activeArtifactCodes || activeArtifactCodes.length === 0) {
    return fromConsentResponse(legacyConsents, cmsConsentSystemTexts);
  }

  const sortedPendingConsents = await getSortedConsentsWithArtifacts({
    activeArtifactCodes,
    locale,
    requests,
    apiOptions,
    logger,
  });

  return fromConsentResponse([...legacyConsents, ...sortedPendingConsents], cmsConsentSystemTexts);
};

/**
 * Get the legacy consents (EULA + PP) and all consents in the active schedule.
 * Filter them on role, platform, features and approved.
 * Get and map artifacts and cms content into the consents.
 * Sort by EULA > PP > mandatory consents true > false
 * @deprecated
 */
const getApprovedConsents = async (
  user: User,
  platform: ConsentPlatform,
  apiOptions?: ApiOptions,
  logger?: CspLogger,
): Promise<ApprovedConsentsV1> => {
  const { locale } = user.localization;
  const role = getUserRole(user);

  const legacyConsents = await LegacyConsentService.getApprovedLegacyConsents(
    user,
    platform,
    [ConsentType.EULA, ConsentType.PRIVACY_POLICY],
    apiOptions,
  );

  const activeSchedules: ConsentSchedule[] = await ConsentScheduleService.getActiveSchedules(
    user.firstOrgId,
    apiOptions,
  );

  const requests = await consentFilterUtil.filterActiveApprovedRequests(
    activeSchedules,
    role,
    platform,
    user,
    apiOptions,
  );

  const activeArtifactCodes = getArtifactCodesFromRequests(requests);

  if (!activeArtifactCodes || activeArtifactCodes.length === 0) {
    return fromApprovedConsentResponse(legacyConsents);
  }

  const sortedApprovedConsents = await getSortedConsentsWithArtifacts({
    activeArtifactCodes,
    locale,
    requests,
    apiOptions,
    logger,
  });

  return fromApprovedConsentResponse([...legacyConsents, ...sortedApprovedConsents]);
};

const getScheduledConsents = async (
  input: GetScheduledConsentsQueryInV1,
  user: User,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  apiOptions?: ApiOptions,
): Promise<ScheduledConsent[]> => {
  try {
    const restOptions = toRestOptions(apiOptions);
    const outcomes = await ConsentRestServiceV1.getConsentActionOutcomesV1(restOptions);
    const consentActionsWithArtifactMeta = await getConsentActionsWithArtifactMeta(platform, user, restOptions);
    const scheduledConsents = toScheduledTextConsents(
      outcomes.consentActionOutcomes,
      consentActionsWithArtifactMeta,
      platform,
      legacyConsentTypes,
      user.roles,
      [],
      input.consentTypesToFilterOut,
      [],
    );

    return scheduledConsents;
  } catch (error) {
    getTelemetryService().trackException({ error: `Consent Response Service: ${error}` });

    throw Error(`Error getting scheduled consents`);
  }
};

const getScheduledConsentsWithContent = async (
  user: User,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  skippedConsents: Array<SkippedConsentInputV1>,
  onFeatures: Maybe<Array<ConsentFeatureTypeV1>>,
  consentTypesToFilterOut: Maybe<Array<string>>,
  apiOptions?: ApiOptions,
): Promise<ScheduledConsentsV1> => {
  try {
    const genericContentPackage = await PatientContentService.fetchGenericPackage(user.localization.locale, apiOptions);
    const staticContent = getPodGenericResourceContentBlock(
      genericContentPackage.resource,
      'appPatientConsentScreen',
      'systemTexts',
    );
    const consentStaticContent = toWebAppSystemTextIntersection(staticContent, platform);
    const restOptions = toRestOptions(apiOptions);
    const outcomes = await ConsentRestServiceV1.getConsentActionOutcomesV1(restOptions);
    const consentActionsWithArtifactMeta = await getConsentActionsWithArtifactMeta(platform, user, restOptions);

    const scheduledConsents = getTextConsents(
      outcomes.consentActionOutcomes,
      consentActionsWithArtifactMeta,
      platform,
      legacyConsentTypes,
      user.roles,
      onFeatures,
      consentTypesToFilterOut,
      skippedConsents,
      genericContentPackage.consent_document,
      genericContentPackage.consent_text,
      consentStaticContent,
    );

    return toGetScheduledConsentsResponse(scheduledConsents, consentStaticContent);
  } catch (error) {
    getTelemetryService().trackException({ error: `Consent Response Service: ${error}` });

    throw Error(`Error getting scheduled consents with content`);
  }
};

export const getUnresolvedConsentsWithContent = async (
  user: User,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[] = [],
  skippedConsents: Array<SkippedConsentInputV1>,
  onFeatures: Maybe<Array<ConsentFeatureTypeV1>>,
  consentTypesToFilterOut: Maybe<Array<string>>,
  apiOptions?: ApiOptions,
): Promise<ScheduledConsentsV1> => {
  try {
    const { consents, content } = await getScheduledConsentsWithContent(
      user,
      platform,
      legacyConsentTypes,
      skippedConsents,
      onFeatures,
      consentTypesToFilterOut,
      apiOptions,
    );
    const scheduledConsentsToPrompt = consents.filter(consentShouldBePrompted);

    return toGetUnresolvedConsentsResponse(scheduledConsentsToPrompt, content);
  } catch (error) {
    getTelemetryService().trackException({ error: `Consent Response Service: ${error}` });

    throw Error(`Error getting unresolved consents with content`);
  }
};

/**
 * Get the legacy consents (EULA + PP) and all consents in the active schedule (non pending).
 * Filter them on role and platform.
 * Get and map artifacts and cms content into the consents.
 * Sort by EULA > PP > mandatory consents true > false
 * @deprecated
 */
const getAllAnsweredConsents = async (
  input: GetAllAnsweredConsentsQueryInV1,
  user: User,
  platform: ConsentPlatform,
  apiOptions?: ApiOptions,
): Promise<AnsweredConsentsV1> => {
  const { locale } = user.localization;
  const role = getUserRole(user);

  const legacyConsents = await LegacyConsentService.getApprovedLegacyConsents(
    user,
    platform,
    [ConsentType.EULA, ConsentType.PRIVACY_POLICY],
    apiOptions,
  );
  const legacyAnsweredConsents = await fromLegacyConsentsToAnsweredConsents({
    legacyConsents,
    user,
    apiOptions,
  });

  const activeSchedules: ConsentSchedule[] = await ConsentScheduleService.getActiveSchedules(
    user.firstOrgId,
    apiOptions,
  );

  const requests: ConsentRequest[] = consentFilterUtil.filterActiveConsentRequestsByRoleAndPlatform(
    activeSchedules,
    role,
    platform,
  );

  const activeArtifactCodes = getArtifactCodesFromRequests(requests);

  const staticContent = await ConsentContentService.getAppSystemTextsForConsent(locale, apiOptions);

  if (!activeArtifactCodes || activeArtifactCodes.length === 0) {
    return fromAnsweredConsentsResponse(legacyAnsweredConsents, staticContent);
  }

  const artifacts = await ConsentArtifactService.getArtifactsByArtifactCodes(activeArtifactCodes, apiOptions);
  const cmsContents = await ConsentContentService.getCmsContentByArtifacts(artifacts, locale, apiOptions);

  const answeredConsents = await fromRequestsToSortedAnsweredConsents({
    requests,
    skippedConsents: input.skippedConsents,
    artifacts,
    cmsContents,
    user,
    apiOptions,
  });

  return fromAnsweredConsentsResponse([...legacyAnsweredConsents, ...answeredConsents], staticContent);
};

interface GetSortedConsentsWithArtifactsArguments {
  activeArtifactCodes: string[];
  locale: string;
  requests: ConsentRequest[];
  apiOptions?: ApiOptions;
  logger?: CspLogger;
}

const getSortedConsentsWithArtifacts = async ({
  activeArtifactCodes,
  locale,
  requests,
  apiOptions,
  logger,
}: GetSortedConsentsWithArtifactsArguments): Promise<ConsentFormV1[]> => {
  const artifacts = await ConsentArtifactService.getArtifactsByArtifactCodes(activeArtifactCodes, apiOptions);
  const cmsContents = await ConsentContentService.getCmsContentByArtifacts(artifacts, locale, apiOptions);

  const consentsWithArtifacts = mapConsentsWithArtifacts({
    activeAppConsents: requests,
    artifacts,
    cmsContents,
    logger,
  });
  return consentsWithArtifacts.sort((a, b) => Number(b.config.mandatory) - Number(a.config.mandatory));
};

const getUnresolvedConsentsFromScheduledConsents = (scheduledConsents: ScheduledConsent[]): ScheduledConsent[] =>
  scheduledConsents.filter(consentShouldBePrompted);

const getArtifactCodesFromRequests = (consentRequests: ConsentRequest[]): Array<string> =>
  consentRequests?.map((request: ConsentRequest) => {
    if (!request.details) {
      throw new CspError(CspErrorType.BAD_STATE, `Missing mandatory configuration for code: ${request.requestCode}`);
    }
    return request.details.artifactCode;
  }) || [];

export const getConsentActionsWithArtifactMeta = async (
  consentPlatform: ConsentPlatform,
  user: User,
  restOptions: RestOptions,
): Promise<ConsentActionWithArtifactMeta[]> => {
  const consentActionsV1 = await ConsentRestServiceV1.queryConsentActionsV1(restOptions, {
    criterion: {
      fieldCriterion: {
        field: ConsentActionCriteriaFieldV1.USER_ID,
        type: ConsentActionQueryTypeV1.EQ,
        values: [user.userId],
      },
    },
  });

  const artifactCodes = uniq([...consentActionsV1.actions.map(a => a.artifactCode)]);
  const allRelevantArtifacts =
    artifactCodes.length > 0
      ? (
          await ConsentRestServiceV1.queryArtifacts(restOptions, {
            criterion: {
              fieldCriterion: {
                field: ConsentArtifactCriteriaFieldV1.ARTIFACT_CODE,
                type: ConsentArtifactQueryTypeV1.IN,
                values: artifactCodes,
              },
            },
          })
        ).artifacts
      : [];
  const artifactsByArtifactCode = keyBy(allRelevantArtifacts, a => a.artifactCode);

  const consentActionsWithArtifactMeta = mapConsentActionV1WithArtifactMeta(
    consentActionsV1.actions,
    artifactsByArtifactCode,
    consentPlatform,
  );

  return consentActionsWithArtifactMeta;
};

export const ConsentResponseService = {
  // @deprecated
  getPendingConsents,
  // @deprecated
  getApprovedConsents,
  // @deprecated
  getAllAnsweredConsents,
  // @deprecated
  getScheduledConsentsWithContent,
  // @deprecated
  getUnresolvedConsentsWithContent,
  getScheduledConsents,
  getConsentActionsWithArtifactMeta,
  getUnresolvedConsentsFromScheduledConsents,
};
